import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { BarChart3, History, Search } from "lucide-react";
import {
  DashboardShell,
  PageHeading,
  ChartCard,
  tooltipStyle,
  fmt,
  fmtRca,
  LoadingPanel,
} from "@/components/DashboardShell";
import { useTradeData } from "@/components/DataProvider";
import { hsOpportunities } from "@/lib/analytics";
import { perspectiveConfig } from "@/lib/perspective";
import { HsClassificationAssistant } from "@/components/HsClassificationAssistant";

export const Route = createFileRoute("/hs")({
  head: () => ({ meta: [{ title: "HS & RCA — Ready4Trade" }] }),
  component: HsPage,
});

function HsPage() {
  const { leads, aggregates, isLoading, perspective } = useTradeData();
  const [query, setQuery] = useState("");
  const [rcaFilter, setRcaFilter] = useState<"all" | "above" | "below" | "missing">("above");
  const config = perspectiveConfig(perspective);
  const rows = useMemo(
    () => (leads ? hsOpportunities(leads, perspective) : []),
    [leads, perspective],
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (rcaFilter === "above" && !((row.rca ?? 0) > 1)) return false;
      if (rcaFilter === "below" && !(row.rca != null && row.rca <= 1)) return false;
      if (rcaFilter === "missing" && row.rca != null) return false;
      if (
        needle &&
        !`${row.code} ${row.description} ${row.sector} ${row.topMarket}`
          .toLowerCase()
          .includes(needle)
      )
        return false;
      return true;
    });
  }, [query, rcaFilter, rows]);

  if (isLoading || !leads || !aggregates)
    return (
      <DashboardShell>
        <LoadingPanel />
      </DashboardShell>
    );
  const top = filtered.slice(0, 20);
  const trend = filtered
    .filter((row) => row.rca != null)
    .slice(0, 8)
    .map((row) => ({
      label: `${row.code} · ${trim(row.description, 23)}`,
      "2022": row.rca2022,
      "2023": row.rca2023,
      "2024": row.rca2024,
    }));

  return (
    <DashboardShell meta={aggregates.meta}>
      <PageHeading
        title={`${config.flag} HS6 demand and RCA`}
        subtitle={`Compare buyer-lead frequency with ${config.country}'s three-year pooled Balassa RCA and inspect annual stability.`}
        badge={`${fmt(filtered.length)} HS6 products`}
      />

      <HsClassificationAssistant />

      <section className="surface-panel p-4 flex flex-wrap gap-3 items-center">
        <div className="relative min-w-[260px] flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search HS6, product, sector or top market…"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-secondary border border-transparent text-sm focus:outline-none focus:border-primary/40"
          />
        </div>
        <select
          value={rcaFilter}
          onChange={(event) => setRcaFilter(event.target.value as typeof rcaFilter)}
          className="filter-select"
        >
          <option value="above">RCA above 1</option>
          <option value="below">RCA 1 or below</option>
          <option value="missing">RCA unavailable</option>
          <option value="all">All products</option>
        </select>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard
          title="Top products by buyer demand"
          subtitle="Current filter, ranked by number of buyer leads"
          icon={<BarChart3 className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={520}>
            <BarChart data={top} layout="vertical" margin={{ left: 10, right: 22 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#cbd5e1" />
              <YAxis
                dataKey={(row: (typeof top)[number]) =>
                  `${row.code} · ${trim(row.description, 30)}`
                }
                type="category"
                width={245}
                tick={{ fontSize: 10, fill: "#334155" }}
                stroke="#cbd5e1"
              />
              <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="Buyer leads" fill="#2563eb" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Annual RCA stability"
          subtitle="2022, 2023 and 2024 RCA for the leading filtered products"
          icon={<History className="w-4 h-4" />}
        >
          <ResponsiveContainer width="100%" height={520}>
            <BarChart data={trend} layout="vertical" margin={{ left: 10, right: 22 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, "auto"]}
                tick={{ fontSize: 10, fill: "#64748b" }}
                stroke="#cbd5e1"
              />
              <YAxis
                dataKey="label"
                type="category"
                width={225}
                tick={{ fontSize: 10, fill: "#334155" }}
                stroke="#cbd5e1"
              />
              <Tooltip contentStyle={tooltipStyle} formatter={(value) => fmtRca(Number(value))} />
              <Legend />
              <Bar dataKey="2022" fill="#94a3b8" />
              <Bar dataKey="2023" fill="#60a5fa" />
              <Bar dataKey="2024" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>

      <section className="surface-panel p-5">
        <h3 className="font-display text-base mb-4">HS6 opportunity table</h3>
        <div className="overflow-auto max-h-[680px] rounded-xl border border-border">
          <table className="w-full text-xs">
            <thead className="bg-secondary text-muted-foreground sticky top-0 z-10">
              <tr>
                <Th>HS6</Th>
                <Th>Product</Th>
                <Th>Sector</Th>
                <Th className="text-right">Pooled RCA</Th>
                <Th className="text-right">2022</Th>
                <Th className="text-right">2023</Th>
                <Th className="text-right">2024</Th>
                <Th className="text-right">Leads</Th>
                <Th className="text-right">Markets</Th>
                <Th>Top market</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.code} className="border-t border-border hover:bg-secondary/40">
                  <td className="px-3 py-2 font-mono font-semibold text-primary">{row.code}</td>
                  <td className="px-3 py-2 min-w-[260px] font-medium">{row.description}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                    {row.sector}
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">
                    {fmtRca(row.rca)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtRca(row.rca2022)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtRca(row.rca2023)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtRca(row.rca2024)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(row.count)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(row.destinations)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.topMarket}</td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={10} className="px-3 py-12 text-center text-muted-foreground">
                    No HS6 products match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </DashboardShell>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-2 text-[10px] uppercase font-semibold tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function trim(value: string, length: number) {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value;
}
